import React, { useState, useCallback, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { auth } from '../firebase/config';
import { onAuthStateChanged } from 'firebase/auth';
import { API_ENDPOINTS, API_BASE_URL, apiClient } from '../config/api';
import './CreateServiceRequestPage.css';
import { SERVICE_TYPE_OPTIONS } from '../data/serviceTypes';
import { getServiceTypeLabel } from '../utils/serviceTypeLabel';
import { toUtcWallClock, toUtcInstant, nowWallClockInViewerZone } from '../utils/dateTime';
import { getActiveRoleFromStorage } from '../utils/getActiveRole';

// Broker-shaped roles skip viewingRole scoping: they need to see all their
// linked agents' transactions, not just ones where they participate directly.
const BROKER_ROLES = ['BROKER', 'BROKER_MANAGER', 'BROKER_ADMIN'];

// ── Static config ─────────────────────────────────────────────────────────────

const URGENCY_LEVELS = [
  { value: 'NORMAL',    label: 'Normal' },
  { value: 'URGENT',    label: 'Urgent' },
  { value: 'EMERGENCY', label: 'Emergency' },
];

const TXN_STATUSES = ['PENDING', 'ACTIVE', 'UNDER_CONTRACT', 'CLOSING'];

// Display name for a service provider. COMPANY-vendor SPs render their
// businessName (person's name is often unset / the admin's); INDIVIDUAL SPs
// render first+last. Falls back gracefully so nothing renders blank.
const resolveProviderName = (prov) => {
  if (!prov) return 'Unknown';
  const person = prov.fullName || prov.name
    || [prov.firstName, prov.lastName].filter(Boolean).join(' ').trim();
  const business = (prov.businessName || '').trim();
  const isCompany = (prov.vendorType || '').toUpperCase() === 'COMPANY';
  if (isCompany && business) return business;
  if (person) return person;
  return business || 'Unknown';
};

// Transaction-status → badge label mapping. PENDING renders as "Prospect"
// (theme-purple pill via `.csr-badge-prospect`). ACTIVE / UNDER_CONTRACT /
// CLOSING all render as "Active" (green) — sub-status granularity lives on
// the transaction detail screen, not the picker. Product 2026-08-26.
const STATUS_BADGE = {
  PENDING:        { label: 'Prospect', cls: 'csr-badge-prospect' },
  ACTIVE:         { label: 'Active',   cls: 'csr-badge-active' },
  UNDER_CONTRACT: { label: 'Active',   cls: 'csr-badge-active' },
  CLOSING:        { label: 'Active',   cls: 'csr-badge-active' },
};

// ── 2D SVG icons ──────────────────────────────────────────────────────────────
const BackIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="15 18 9 12 15 6"/>
  </svg>
);

const SearchIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
  </svg>
);

// ── Helpers ───────────────────────────────────────────────────────────────────
const getInitials = (name = '') =>
  name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2) || '?';

const AVATAR_COLORS = ['#6c5ce7', '#9C27B0', '#3F51B5', '#009688', '#2196F3', '#FF5722', '#795548'];
const avatarColor = (name = '') => {
  const sum = name.split('').reduce((s, c) => s + c.charCodeAt(0), 0);
  return AVATAR_COLORS[sum % AVATAR_COLORS.length];
};

// ── Component ─────────────────────────────────────────────────────────────────
const CreateServiceRequestPage = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Set only when arriving from ChecklistModal's "Raise Service Request"
  // (TransactionTimeline.js navigates here with this in route state). Every
  // other entry point into this page passes no state, so all of the prefill
  // logic below is a no-op for the normal manual-creation flow.
  const [checklistPrefill] = useState(() => location.state?.fromChecklist || null);
  const [checklistPrefillApplied, setChecklistPrefillApplied] = useState(false);
  // Set when arriving from the Agent Calendar's "Raise service request" link.
  // Shape: { eventType, scheduledDate: 'yyyy-mm-dd', transactionId }. Three
  // independent "apply once" flags because the three pieces land at different
  // times — date is synchronous, service type waits on the catalog fetch, and
  // the transaction picker waits on loadTransactions() to populate.
  const [calendarPrefill] = useState(() => location.state?.fromCalendar || null);
  const [calendarDatePrefillApplied, setCalendarDatePrefillApplied] = useState(false);
  const [calendarTypePrefillApplied, setCalendarTypePrefillApplied] = useState(false);
  const [calendarTxnPrefillApplied, setCalendarTxnPrefillApplied] = useState(false);

  // view: 'form' | 'property' | 'provider'
  const [view, setView] = useState('form');

  // Form fields
  const [serviceType,      setServiceType]      = useState('');
  // Optional free-text service name, shown only when serviceType is "Other".
  // Sent as customServiceType when filled; left blank, the SR goes out as plain
  // "Other" exactly as before. serviceType itself always stays canonical.
  const [customServiceType, setCustomServiceType] = useState('');
  const isOtherServiceType = serviceType.toLowerCase() === 'other';
  const serviceTypeLabel = getServiceTypeLabel({ serviceType, customServiceType });
  // Service-type picker catalog — seeded with the offline fallback, overwritten
  // by the live GET /api/v1/service-types fetch below (mirrors mobile's
  // CreateServiceRequestScreen).
  const [serviceTypeOptions, setServiceTypeOptions] = useState(SERVICE_TYPE_OPTIONS);
  const [propertyData,     setPropertyData]     = useState(null);
  // Pre-fill with "now" as wall-clock date/time in the viewer's own profile
  // timezone (not the device's) — same fix as the Transaction Timeline's
  // "Add to Calendar" modal. Computed once on mount via a lazy initializer so
  // the date/time pair and the `min` attribute below all agree with each
  // other (no risk of two separate "now" reads straddling a boundary).
  const [nowInViewerZone] = useState(() => nowWallClockInViewerZone());
  const [requestedDate,    setRequestedDate]    = useState(nowInViewerZone.localDate);
  // Due By Time field was removed from the UI. Backend now accepts a null
  // `requestedTime` (@NotNull was dropped), so we skip sending it entirely
  // instead of pinning a synthetic 23:59.
  const [urgencyLevel,     setUrgencyLevel]     = useState('NORMAL');
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [description,      setDescription]      = useState('');
  const [estimatedAmount,  setEstimatedAmount]  = useState('');
  const [errors,           setErrors]           = useState({});
  const [submitting,       setSubmitting]       = useState(false);
  const [submitError,      setSubmitError]      = useState('');
  const [submitSuccess,    setSubmitSuccess]    = useState(false);

  // Property selector state
  const [transactions, setTransactions] = useState([]);
  const [txnSearch,    setTxnSearch]    = useState('');
  const [loadingTxns,  setLoadingTxns]  = useState(false);

  // Provider selector state
  const [providers,    setProviders]    = useState([]);
  const [provSearch,   setProvSearch]   = useState('');
  const [loadingProvs, setLoadingProvs] = useState(false);
  // Debounced mirror of provSearch so typing doesn't hammer the backend
  // on every keystroke. The debounced value also drives the radius flip:
  // when the requester starts searching by name we assume they want
  // matches from anywhere, not just within 25 mi.
  const [debouncedProvSearch, setDebouncedProvSearch] = useState('');
  useEffect(() => {
    const id = setTimeout(() => setDebouncedProvSearch(provSearch.trim()), 300);
    return () => clearTimeout(id);
  }, [provSearch]);
  // Backend requires >= 3 chars on the `name` filter (see UserController
  // .listServiceProviderUsers). Below that we ignore the query — client-
  // side substring filter still narrows the fetched list for immediate
  // feedback.
  const nameQuery = debouncedProvSearch.length >= 3 ? debouncedProvSearch : '';

  const getFirebaseToken = () =>
    new Promise((resolve, reject) => {
      if (auth.currentUser) {
        auth.currentUser.getIdToken(true).then(resolve).catch(reject);
      } else {
        const unsub = onAuthStateChanged(auth, (user) => {
          unsub();
          if (user) user.getIdToken(true).then(resolve).catch(reject);
          else reject(new Error('Not authenticated'));
        });
        setTimeout(() => reject(new Error('Auth timeout')), 10000);
      }
    });

  // Fetch the canonical service-type catalog once on mount. The fallback
  // list already seeds the picker so it's usable offline; this overwrites it
  // as soon as the response lands (mirrors mobile's CreateServiceRequestScreen).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = await getFirebaseToken();
        const resp = await apiClient.get(API_ENDPOINTS.serviceTypes, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const catalog = Array.isArray(resp.data) ? resp.data : (resp.data?.content || resp.data?.data || []);
        if (!cancelled && catalog.length > 0) {
          setServiceTypeOptions(catalog.map(t => ({ id: t.id, name: t.label })));
        }
      } catch (error) {
        console.warn('[CreateServiceRequest] Failed to fetch service types, using fallback:', error);
      }
    })();
    return () => { cancelled = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Applies the checklist-item prefill once serviceTypeOptions has a value to
  // match against (the fallback list is present from first render, so this
  // typically runs immediately — it re-checks if the live catalog fetch above
  // changes the list, but only until it has successfully applied once, so it
  // never clobbers a value the requester has since edited by hand).
  useEffect(() => {
    if (!checklistPrefill || checklistPrefillApplied) return;
    setPropertyData({
      id: checklistPrefill.transactionId || null,
      address: checklistPrefill.propertyAddress || '',
      city: checklistPrefill.propertyCity || '',
      state: checklistPrefill.propertyState || '',
      transactionNumber: '',
      status: '',
    });
    const suggested = (checklistPrefill.suggestedServiceType || '').trim();
    const matched = serviceTypeOptions.find(
      opt => opt.name.toLowerCase() === suggested.toLowerCase()
    );
    if (matched) {
      setServiceType(matched.name);
      setDescription(checklistPrefill.notes || '');
    } else {
      // No catalog match — default to "Other" and fold the parser's raw
      // suggestion into the description instead of forcing a bad match.
      setServiceType('Other');
      setCustomServiceType(suggested.slice(0, 100));
      setDescription(
        suggested
          ? `Suggested service: ${suggested} — ${checklistPrefill.notes || ''}`
          : (checklistPrefill.notes || '')
      );
    }
    setChecklistPrefillApplied(true);
  }, [checklistPrefill, checklistPrefillApplied, serviceTypeOptions]);

  // ── Load transactions ─────────────────────────────────────────────────────
  const loadTransactions = useCallback(async () => {
    setLoadingTxns(true);
    try {
      const token = await getFirebaseToken();
      if (!token) return;
      const statusParams = TXN_STATUSES.map(s => `status=${s}`).join('&');
      // size=100 (backend MAX_PAGE_SIZE) so ACTIVE / UNDER_CONTRACT / CLOSING
      // txns don't get pushed off the first page by a surge of recent
      // PENDING (Prospect) rows — createdAt DESC + default size=20 was
      // hiding older active deals from the picker.
      // Buying Prospect = PENDING + side=BUYING; backend hides these from SR
      // pickers so an agent can't file a Service Request against a lead. The
      // status list above still keeps Listing Prospects (PENDING + LISTING).
      // viewingRole scopes the picker to the user's current role (mirroring
      // mobile SelectPropertyScreen). Brokers skip the scope so they can pick
      // any of their linked agents' transactions.
      const activeRole = getActiveRoleFromStorage();
      const roleParam = activeRole && !BROKER_ROLES.includes(activeRole)
        ? `&viewingRole=${activeRole}`
        : '';
      const resp = await apiClient.get(
        `${API_ENDPOINTS.transactions}?${statusParams}&size=100&sortBy=createdAt&sortOrder=desc&excludeBuyingProspects=true${roleParam}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const d = resp.data;
      const list = Array.isArray(d) ? d : (d.content || d.data?.content || d.data || []);
      setTransactions(list);
    } catch { /* silent */ } finally { setLoadingTxns(false); }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Load providers ────────────────────────────────────────────────────────
  const loadProviders = useCallback(async () => {
    if (!serviceType) { setProviders([]); return; }
    setLoadingProvs(true);
    try {
      const token = await getFirebaseToken();
      if (!token) return;
      // Phase-4 SP-radius switch. Prefer the linked property's coords
      // (propertyData.latitude/longitude — carried from txn.propertyLatitude
      // via handleSelectProperty), fall back to the requester's own coords,
      // then finally to the pre-radius state predicate so this picker never
      // dead-ends. Backend applies a 25-mi Haversine filter when either
      // coord pair is present.
      // Reject exact-zero as "not populated" — (0, 0) is the Java Double
      // default for a coord column that never got stamped, not a real US
      // property or agent address. Without this guard, a transaction with
      // propertyLatitude=0 would trigger radius mode with `referenceLat=0`
      // and filter every SP against the Gulf of Guinea.
      const nz = (v) => {
        const n = Number(v);
        return Number.isFinite(n) && n !== 0 ? n : null;
      };
      const propertyLat = nz(propertyData?.latitude);
      const propertyLng = nz(propertyData?.longitude);
      let requesterLat = null;
      let requesterLng = null;
      let stateParam;
      try {
        const u = JSON.parse(localStorage.getItem('user') || '{}');
        requesterLat = nz(u.latitude);
        requesterLng = nz(u.longitude);
        if (propertyData?.state) {
          stateParam = propertyData.state;
        } else {
          stateParam = u.licenseState || u.stateCode || u.state || undefined;
        }
      } catch {
        // localStorage parse failure — fall through with everything unset.
      }
      // Typing a name query widens the search: drop the coord filter so the
      // backend runs the state predicate (or no filter) and returns matches
      // from a broader pool. Empty query keeps the property → requester
      // coord fallback so the initial list stays scoped to the 25-mi radius.
      const useCoords = !nameQuery && propertyLat != null && propertyLng != null;
      const useRequesterCoords = !nameQuery && !useCoords && requesterLat != null && requesterLng != null;
      // Prefer the catalog id (rename-safe) over the label. Dropdown stores the
      // label as its <option value> for legacy display reasons; resolve back to
      // the id here so we match the mobile picker's wire format and the backend's
      // preferred contract. Falls back to the label itself if the catalog didn't
      // load (backend still accepts the canonical label for back-compat).
      const serviceTypeParam =
        serviceTypeOptions.find(o => o.name === serviceType)?.id || serviceType;
      const resp = await apiClient.get(API_ENDPOINTS.serviceProviders, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          serviceType: serviceTypeParam,
          ...(useCoords ? { referenceLat: propertyLat, referenceLng: propertyLng } : {}),
          ...(useRequesterCoords ? { referenceLat: requesterLat, referenceLng: requesterLng } : {}),
          ...(!useCoords && !useRequesterCoords && stateParam ? { state: stateParam } : {}),
          ...(nameQuery ? { name: nameQuery } : {}),
        },
      });
      const d = resp.data;
      const list = Array.isArray(d) ? d : (d.content || d.data || []);
      setProviders(list);
    } catch { setProviders([]); } finally { setLoadingProvs(false); }
  }, [serviceType, propertyData?.state, propertyData?.latitude, propertyData?.longitude, nameQuery]); // eslint-disable-line react-hooks/exhaustive-deps

  const openPropertyView = () => {
    setView('property');
    if (transactions.length === 0) loadTransactions();
  };

  const openProviderView = () => {
    setView('provider');
    loadProviders();
  };

  // Re-fetch when the debounced search flips into name-query mode (or back
  // out). loadProviders is memoized on nameQuery, so this useEffect fires
  // exactly once per debounced transition — the backend re-runs with the
  // `name` param (coords dropped) or reverts to the coord path when the
  // search clears.
  useEffect(() => {
    if (view === 'provider') loadProviders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nameQuery]);

  const handleSelectProperty = (txn) => {
    const address = txn.propertyAddress || txn.transactionName || txn.dealName || '';
    setPropertyData({
      id: txn.id,
      address,
      city:  txn.propertyCity  || '',
      state: txn.propertyState || '',
      // Phase-4 SP-radius — carry the transaction's geocoded property coords
      // (Phase-2 on transaction-service) so loadProviders can pass them to
      // /users/service-providers as referenceLat/Lng instead of the state
      // predicate. Nullable — a transaction without a linked property (or one
      // created before Phase 2 rolled out) has null coords and the picker
      // falls back to the state filter via propertyData.state. Zero is
      // treated as null — see the `nz` guard in loadProviders for why
      // (Java Double column default, not a real US coord).
      latitude:  (Number(txn.propertyLatitude)  || 0) !== 0 ? Number(txn.propertyLatitude)  : null,
      longitude: (Number(txn.propertyLongitude) || 0) !== 0 ? Number(txn.propertyLongitude) : null,
      transactionNumber: txn.transactionNumber || '',
      status: txn.status || '',
    });
    if (selectedProvider && (txn.propertyCity || '') !== propertyData?.city) setSelectedProvider(null);
    setErrors(p => ({ ...p, propertyData: '' }));
    setView('form');
    setTxnSearch('');
  };

  // ── Agent Calendar prefill ────────────────────────────────────────────────
  // Three independent one-shot effects, each guarded by its own flag:
  //   1. requestedDate — synchronous, applies as soon as calendarPrefill lands.
  //   2. serviceType   — waits on serviceTypeOptions (same pattern as the
  //                      checklist effect above). Falls back to "Other" when
  //                      the catalog doesn't carry the eventType label.
  //   3. propertyData  — waits on transactions to populate; eager-loads them
  //                      when we land here with a transactionId so the user
  //                      doesn't have to open the picker first.
  useEffect(() => {
    if (!calendarPrefill?.scheduledDate || calendarDatePrefillApplied) return;
    const ok = /^\d{4}-\d{2}-\d{2}$/.test(String(calendarPrefill.scheduledDate));
    if (ok) setRequestedDate(calendarPrefill.scheduledDate);
    setCalendarDatePrefillApplied(true);
  }, [calendarPrefill, calendarDatePrefillApplied]);

  useEffect(() => {
    if (!calendarPrefill?.eventType || calendarTypePrefillApplied || serviceTypeOptions.length === 0) return;
    const suggested = String(calendarPrefill.eventType).trim();
    const matched = serviceTypeOptions.find(
      (opt) => opt.name.toLowerCase() === suggested.toLowerCase()
    );
    if (matched) {
      setServiceType(matched.name);
    } else {
      // Catalog miss — fall back to "Other" + stash the raw calendar label in
      // description so the SR carries the context the agent picked.
      setServiceType('Other');
      setCustomServiceType(suggested.slice(0, 100));
      setDescription((prev) => prev || `From calendar: ${suggested}`);
    }
    setCalendarTypePrefillApplied(true);
  }, [calendarPrefill, calendarTypePrefillApplied, serviceTypeOptions]);

  // Eager-load transactions as soon as we see a calendar transactionId —
  // normally this fires when the user opens the property picker, but we need
  // the list ready so the next effect can match the id into propertyData
  // without a round-trip through the picker UI.
  useEffect(() => {
    if (!calendarPrefill?.transactionId || calendarTxnPrefillApplied) return;
    if (transactions.length === 0 && !loadingTxns) loadTransactions();
  }, [calendarPrefill, calendarTxnPrefillApplied, transactions.length, loadingTxns, loadTransactions]);

  useEffect(() => {
    if (!calendarPrefill?.transactionId || calendarTxnPrefillApplied || transactions.length === 0) return;
    const txn = transactions.find((t) => String(t.id) === String(calendarPrefill.transactionId));
    if (txn) handleSelectProperty(txn);
    // Mark applied regardless of whether we found a match — a closed or
    // out-of-scope transaction stays null and the user can pick manually.
    setCalendarTxnPrefillApplied(true);
  }, [calendarPrefill, calendarTxnPrefillApplied, transactions]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSelectProvider = (prov) => {
    setSelectedProvider({
      id: prov.id || prov.providerId || prov.serviceProviderId || prov.userId || prov.firebaseUid,
      fullName: resolveProviderName(prov),
      serviceType: prov.serviceType || '',
      city: prov.city || '',
    });
    setErrors(p => ({ ...p, provider: '' }));
    setView('form');
    setProvSearch('');
  };

  // ── Validate & submit ─────────────────────────────────────────────────────
  // Service Provider + Service Description are optional — the requester can
  // create an "open" SR (no provider picked yet) and assign later from the
  // details modal. Backend accepts null/blank on both; DTO @NotBlank on
  // providerId + description was dropped 2026-09-08 for this flow.
  const validate = () => {
    const e = {};
    if (!serviceType)        e.serviceType   = 'Service type is required.';
    if (!propertyData)       e.propertyData  = 'Property address is required.';
    if (!requestedDate)      e.requestedDate = 'Date is required.';
    // Provider is optional now — only validate the shape when one WAS
    // selected (a picker glitch shouldn't silently ship a partial object).
    if (selectedProvider && !selectedProvider.id) {
      e.provider = 'Selected provider has no valid ID. Please choose again.';
    }
    return e;
  };

  const handleSubmit = async () => {
    setSubmitError('');
    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;

    setSubmitting(true);
    try {
      const token = await getFirebaseToken();
      const authHeaders = { Authorization: `Bearer ${token}` };

      // Treat the picker values as wall-clock time in the viewer's own
      // profile timezone (not the device's timezone — a device clock set to
      // the wrong zone must not silently shift what gets saved) and convert
      // to a real UTC instant. Same fix as the write sites in
      // TransactionDetailPage.js.
      const requestedDateISO = toUtcInstant(requestedDate, null);

      // Match TransactionDetailPage's Add-to-Calendar orchestration so a SR
      // raised from the Dashboard shows up on the Calendar page identically
      // to ones raised from a transaction's timeline: create the calendar
      // event first, then the SR, then back-link the SR id onto the event.
      // transactionId is optional — orphan (no-transaction) SRs still get
      // a calendar entry. selectedProvider is optional too (open-SR flow) —
      // guard every read so we don't NPE on the invitees / notify hand-off.
      const invitees = [];
      if (selectedProvider?.fullName?.trim()) {
        invitees.push({ name: selectedProvider.fullName.trim() });
      }
      // Save the "Due By" date/time as UTC: convert the picker's local
      // wall-clock values to their UTC-equivalent wall-clock strings and mark
      // the event with timeZone: "UTC" so any viewer renders it correctly
      // converted into their own profile timezone instead of reading these
      // raw digits as if they were already in their own local time.
      const { scheduledDate: utcScheduledDate, scheduledTime: utcScheduledTime } =
        toUtcWallClock(requestedDate, null);
      // eventType stays the canonical serviceType ("Other") — timeline/calendar
      // correlate SR ↔ event on it. Only the visible title uses the custom name.
      const serviceEventPayload = {
        title: serviceTypeLabel,
        scheduledDate: utcScheduledDate,
        scheduledTime: utcScheduledTime || null,
        timeZone: 'UTC',
        duration: '1 hour',
        eventType: serviceType,
        allDay: false,
        category: 'service',
        description: description.trim(),
        location: propertyData.address,
        ...(propertyData.id && { transactionId: propertyData.id }),
        ...(invitees.length && { invitees }),
      };
      const eventRes = await apiClient.post(
        `${API_BASE_URL}/api/v1/calendar/events`,
        serviceEventPayload,
        { headers: authHeaders },
      );
      const createdEvent = eventRes.data?.data ?? eventRes.data;

      const parsedEstimate = estimatedAmount !== ''
        ? Number(estimatedAmount)
        : null;
      // providerId + description are optional server-side (open-SR flow) —
      // omit rather than sending "" so the backend's blank-normalisation
      // path is used only for the intended cases.
      const trimmedDescription = description.trim();
      const trimmedCustomServiceType = customServiceType.trim();
      // Atomic back-link via user-service (2026-09-11): pass calendarEventId
      // in the create payload and user-service calls calendar-service
      // internally to write the SR ↔ event linkage. Replaces the previous
      // fragile client-side PUT that could race or fail silently.
      const srRes = await apiClient.post(API_ENDPOINTS.createServiceRequest, {
        serviceType,
        ...(isOtherServiceType && trimmedCustomServiceType && { customServiceType: trimmedCustomServiceType }),
        propertyAddress: propertyData.address,
        requestedDate:   requestedDateISO,
        urgencyLevel,
        transactionId:   propertyData.id || undefined,
        ...(selectedProvider?.id && { providerId: selectedProvider.id }),
        ...(trimmedDescription && { description: trimmedDescription }),
        ...(parsedEstimate !== null && Number.isFinite(parsedEstimate) && parsedEstimate >= 0 && { estimatedAmount: parsedEstimate }),
        ...(createdEvent?.eventId && { calendarEventId: createdEvent.eventId }),
      }, { headers: authHeaders });

      setSubmitSuccess(true);
      setTimeout(() => navigate('/dashboard'), 3000);
    } catch (err) {
      setSubmitError(err?.response?.data?.message || 'Failed to create service request. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // ── Filtered lists ────────────────────────────────────────────────────────
  const filteredTxns = transactions.filter(txn => {
    if (!txnSearch.trim()) return true;
    const q = txnSearch.toLowerCase();
    return (txn.propertyAddress || txn.transactionName || '').toLowerCase().includes(q) ||
           (txn.transactionNumber || '').toLowerCase().includes(q);
  });

  // Client-side filter is a first-glance narrowing before the backend
  // debounced-3-char refetch kicks in. When nameQuery is already active,
  // the backend already filtered against firstName/lastName/fullName/
  // businessName/email — running our own weaker match here would silently
  // drop legitimate hits, so short-circuit and trust the backend.
  // The backend response has firstName/lastName/businessName/email — NOT
  // the legacy `fullName`/`name` fields the old client filter checked
  // against, which is why "ashu" over-filtered to zero when the backend
  // returned 4 rows.
  const filteredProvs = providers.filter(prov => {
    if (nameQuery) return true;
    const q = provSearch.trim().toLowerCase();
    if (!q) return true;
    const full = [prov.firstName, prov.lastName].filter(Boolean).join(' ').toLowerCase();
    return (
      full.includes(q)
      || (prov.firstName || '').toLowerCase().includes(q)
      || (prov.lastName || '').toLowerCase().includes(q)
      || (prov.businessName || '').toLowerCase().includes(q)
      || (prov.email || '').toLowerCase().includes(q)
    );
  });


  // ── Property sub-view ─────────────────────────────────────────────────────
  if (view === 'property') {
    return (
      <div className="csr-container">
        <div className="csr-main-content">
          <div className="csr-content-box">
            <div className="csr-subview-header">
              <button type="button" className="csr-back-btn" onClick={() => { setView('form'); setTxnSearch(''); }}>
                <BackIcon />
              </button>
              <div>
                <h1 className="csr-page-title">Select Property</h1>
                <p className="csr-page-subtitle">Choose from your active transactions</p>
              </div>
            </div>

            <div className="csr-form-card">
              <div className="csr-form-section">
                <div className="csr-form-group">
                  <div className="csr-search-wrapper">
                    <span className="csr-search-icon"><SearchIcon /></span>
                    <input
                      className="csr-form-input csr-search-input"
                      type="text"
                      placeholder="Search by address or deal name..."
                      value={txnSearch}
                      onChange={e => setTxnSearch(e.target.value)}
                    />
                  </div>
                </div>

                {loadingTxns ? (
                  <p className="csr-list-empty">Loading properties…</p>
                ) : filteredTxns.length === 0 ? (
                  <p className="csr-list-empty">{txnSearch ? 'No matching properties.' : 'No active transactions found.'}</p>
                ) : (
                  <>
                    <p className="csr-list-count">{filteredTxns.length} {filteredTxns.length === 1 ? 'property' : 'properties'} found</p>
                    {filteredTxns.map(txn => {
                      const addr      = txn.propertyAddress || txn.transactionName || txn.dealName || 'Unknown Address';
                      const cityState = [txn.propertyCity, txn.propertyState].filter(Boolean).join(', ');
                      const badge     = STATUS_BADGE[txn.status] || { label: txn.status, cls: 'csr-badge-prospect' };
                      return (
                        <div
                          key={txn.id}
                          className="csr-list-card"
                          role="button"
                          tabIndex={0}
                          onClick={() => handleSelectProperty(txn)}
                          onKeyDown={e => e.key === 'Enter' && handleSelectProperty(txn)}
                        >
                          <div className="csr-list-card-icon">🏠</div>
                          <div className="csr-list-card-content">
                            <div className="csr-list-card-title">{addr}</div>
                            {cityState && <div className="csr-list-card-desc">{cityState}</div>}
                            {txn.transactionName && (
                              <div className="csr-list-card-meta">{txn.transactionName}</div>
                            )}
                          </div>
                          <span className={`csr-status-badge ${badge.cls}`}>{badge.label}</span>
                          <div className="csr-list-card-arrow">→</div>
                        </div>
                      );
                    })}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Provider sub-view ─────────────────────────────────────────────────────
  if (view === 'provider') {
    const city = propertyData?.city || '';
    return (
      <div className="csr-container">
        <div className="csr-main-content">
          <div className="csr-content-box">
            <div className="csr-subview-header">
              <button type="button" className="csr-back-btn" onClick={() => { setView('form'); setProvSearch(''); }}>
                <BackIcon />
              </button>
              <div>
                <h1 className="csr-page-title">Select Provider</h1>
                <p className="csr-page-subtitle">{[serviceTypeLabel, city].filter(Boolean).join(' · ') || 'Browse service providers'}</p>
              </div>
            </div>

            <div className="csr-form-card">
              <div className="csr-form-section">
                <div className="csr-form-group">
                  <div className="csr-search-wrapper">
                    <span className="csr-search-icon"><SearchIcon /></span>
                    <input
                      className="csr-form-input csr-search-input"
                      type="text"
                      placeholder="Search by name to look outside 25 mi..."
                      value={provSearch}
                      onChange={e => setProvSearch(e.target.value)}
                    />
                  </div>
                </div>

                {loadingProvs ? (
                  <p className="csr-list-empty">Loading providers…</p>
                ) : filteredProvs.length === 0 ? (
                  <div className="csr-list-empty">
                    <p>
                      {nameQuery
                        ? `No providers found matching "${nameQuery}".`
                        : provSearch
                          ? 'Keep typing (min 3 characters) to search all providers.'
                          : 'No providers found near this property.'}
                    </p>
                    <button
                      type="button"
                      className="csr-btn-primary csr-invite-btn"
                      onClick={() => {
                        // Mirror of AssignServiceProviderModal / TransactionDetailPage
                        // provider picker: let the requester reach the generic
                        // /invite-someone flow (SERVICE_PROVIDER prefill) directly
                        // from the empty state, without backing out to the dashboard.
                        navigate('/invite-someone', { state: { prefillRole: 'SERVICE_PROVIDER' } });
                      }}
                    >
                      Invite a Provider
                    </button>
                  </div>
                ) : (
                  <>
                    <p className="csr-list-count">
                      {nameQuery
                        ? `${filteredProvs.length} matching provider${filteredProvs.length !== 1 ? 's' : ''}`
                        : `${filteredProvs.length} provider${filteredProvs.length !== 1 ? 's' : ''} found nearby${city ? ` in ${city}` : ''}`}
                    </p>
                    {filteredProvs.map(prov => {
                      const id       = prov.id || prov.firebaseUid;
                      const name     = resolveProviderName(prov);
                      const svcLabel = prov.serviceType || serviceType || '';
                      return (
                        <div
                          key={id}
                          className="csr-list-card"
                          role="button"
                          tabIndex={0}
                          onClick={() => handleSelectProvider(prov)}
                          onKeyDown={e => e.key === 'Enter' && handleSelectProvider(prov)}
                        >
                          <div className="csr-provider-avatar" style={{ background: avatarColor(name) }}>
                            {getInitials(name)}
                          </div>
                          <div className="csr-list-card-content">
                            <div className="csr-list-card-title-row">
                              <span className="csr-list-card-title">{name}</span>
                              {(prov.vendorType || '').toUpperCase() === 'COMPANY' && (
                                <span className="csr-company-badge">
                                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <rect x="3" y="3" width="18" height="18" rx="2"/>
                                    <line x1="9" y1="9" x2="9.01" y2="9"/>
                                    <line x1="15" y1="9" x2="15.01" y2="9"/>
                                    <line x1="9" y1="15" x2="9.01" y2="15"/>
                                    <line x1="15" y1="15" x2="15.01" y2="15"/>
                                  </svg>
                                  Company
                                </span>
                              )}
                            </div>
                            {svcLabel && <div className="csr-list-card-desc">{svcLabel}</div>}
                            {(() => {
                              // Locate the SP for the requester's eye. Prefer distance
                              // when the backend is in radius mode; otherwise show the
                              // most specific location we have — "city, state" if
                              // populated, else the state alone, else the licensed
                              // state (from User.licenseState — the identifier the SP
                              // registered under even when their address is blank).
                              // Guard: Number(null) coerces to 0 (not NaN), so a naked
                              // isFinite(Number(...)) check would render "0.0 mi away" on
                              // every state-path row (distanceMiles is null there).
                              // Require a real number type OR a non-null non-empty value.
                              const rawDist = prov.distanceMiles;
                              const dist = (rawDist != null && rawDist !== '' && Number.isFinite(Number(rawDist)))
                                ? `${Number(rawDist).toFixed(1)} mi away`
                                : null;
                              const cityState = [prov.city, prov.state].filter(Boolean).join(', ');
                              const location = cityState || prov.state || prov.licenseState;
                              return dist ? (
                                <>
                                  <div className="csr-list-card-meta csr-list-card-distance">{dist}</div>
                                  {location && <div className="csr-list-card-meta">{location}</div>}
                                </>
                              ) : location ? (
                                <div className="csr-list-card-meta">{location}</div>
                              ) : null;
                            })()}
                          </div>
                          <div className="csr-list-card-arrow">→</div>
                        </div>
                      );
                    })}
                    {!nameQuery && !provSearch && (
                      <p className="csr-list-hint">
                        Not finding what you need? Search by name to look anywhere.
                      </p>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Success screen ────────────────────────────────────────────────────────
  if (submitSuccess) {
    return (
      <div className="csr-container">
        <div className="csr-main-content">
          <div className="csr-content-box">
            <div className="csr-success-screen">
              <div className="csr-success-icon">✓</div>
              <h1 className="csr-success-title">Request Submitted!</h1>
              <p className="csr-success-subtitle">
                {selectedProvider
                  ? "Your service request has been sent to the provider. You'll be notified when they respond."
                  : "Your service request is open. Assign a provider anytime from the transaction timeline."}
              </p>

              <div className="csr-transaction-summary">
                <div className="csr-summary-section">
                  <div className="csr-summary-section-title">Request Details</div>
                  <div className="csr-summary-grid">
                    <div className="csr-summary-item">
                      <span className="csr-summary-label">Service Type</span>
                      <span className="csr-summary-value">{serviceTypeLabel}</span>
                    </div>
                    <div className="csr-summary-item">
                      <span className="csr-summary-label">Urgency</span>
                      <span className="csr-summary-value csr-status-badge-inline">{urgencyLevel}</span>
                    </div>
                    {selectedProvider && (
                      <div className="csr-summary-item">
                        <span className="csr-summary-label">Provider</span>
                        <span className="csr-summary-value">{selectedProvider.fullName}</span>
                      </div>
                    )}
                    <div className="csr-summary-item">
                      <span className="csr-summary-label">Date</span>
                      <span className="csr-summary-value">{requestedDate}</span>
                    </div>
                  </div>
                </div>
                {propertyData && (
                  <div className="csr-summary-section">
                    <div className="csr-summary-section-title">Property</div>
                    <div className="csr-summary-full-address">{propertyData.address}</div>
                  </div>
                )}
              </div>

              <button className="csr-view-btn" onClick={() => navigate('/dashboard')}>
                Back to Dashboard
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Main form ─────────────────────────────────────────────────────────────
  return (
    <div className="csr-container">
      <div className="csr-main-content">
        <div className="csr-content-box">
          <div className="csr-page-header">
            <h1 className="csr-page-title">Create Service Request</h1>
            <p className="csr-page-subtitle">
              {checklistPrefill
                ? <>Request a service provider for your transaction — pre-filled from checklist item: <b>{checklistPrefill.areaOrItemName}</b></>
                : 'Request a service provider for your transaction'}
            </p>
          </div>

          {checklistPrefill && (
            <div className="csr-prefill-banner">
              ✨ Service Type and Service Description below were pre-filled from the checklist finding. Edit anything before submitting.
            </div>
          )}

          <div className="csr-form-card">
            <div className="csr-form-section">
              <div className="csr-form-section-header"> Service Details</div>

              {/* Service Type */}
              <div className="csr-form-group">
                <label className="csr-form-label">Service Type *</label>
                <select
                  className={`csr-form-input csr-form-select ${errors.serviceType ? 'csr-input-error' : ''}`}
                  value={serviceType}
                  onChange={e => { setServiceType(e.target.value); setCustomServiceType(''); setSelectedProvider(null); setProvSearch(''); setErrors(p => ({ ...p, serviceType: '' })); }}
                >
                  <option value="">Select service type…</option>
                  {serviceTypeOptions.map(opt => (
                    <option key={opt.id} value={opt.name}>{opt.name}</option>
                  ))}
                </select>
                {errors.serviceType && <span className="csr-field-error">{errors.serviceType}</span>}
              </div>

              {/* "Other" → requester names the service themselves */}
              {isOtherServiceType && (
                <div className="csr-form-group">
                  <label className="csr-form-label">Specify Service Type (optional)</label>
                  <input
                    type="text"
                    className="csr-form-input"
                    value={customServiceType}
                    maxLength={100}
                    placeholder="e.g. Solar Panel Inspection"
                    onChange={e => setCustomServiceType(e.target.value)}
                  />
                </div>
              )}

              {/* Property Address — locked (not editable) when arriving from a checklist
                  item: the address is derived from the item's own transaction, not a
                  free choice, same as the wireframe's 🔒 behavior. */}
              <div className="csr-form-group">
                <label className="csr-form-label">Property Address *</label>
                <div className="csr-browse-wrapper">
                  <input
                    type="text"
                    className={`csr-form-input csr-browse-input ${errors.propertyData ? 'csr-input-error' : ''} ${checklistPrefill ? 'csr-browse-input-locked' : ''}`}
                    value={propertyData ? [propertyData.address, propertyData.city, propertyData.state].filter(Boolean).join(', ') : ''}
                    readOnly
                    placeholder="Select a property from your transactions"
                    onClick={checklistPrefill ? undefined : openPropertyView}
                  />
                  {checklistPrefill ? (
                    <span className="csr-locked-icon" title="Derived from the checklist item's transaction">🔒</span>
                  ) : (
                    <button type="button" className="csr-browse-btn" onClick={openPropertyView}>
                      {propertyData ? 'Change' : 'Browse'}
                    </button>
                  )}
                </div>
                {errors.propertyData && <span className="csr-field-error">{errors.propertyData}</span>}
              </div>

              {/* Due By Date (time input removed; deadline is end-of-day) */}
              <div className="csr-form-group">
                <label className="csr-form-label">Due By Date *</label>
                <input
                  type="date"
                  className={`csr-form-input ${errors.requestedDate ? 'csr-input-error' : ''}`}
                  value={requestedDate}
                  min={nowInViewerZone.localDate}
                  onChange={e => { setRequestedDate(e.target.value); setErrors(p => ({ ...p, requestedDate: '' })); }}
                />
                {errors.requestedDate && <span className="csr-field-error">{errors.requestedDate}</span>}
              </div>

              {/* Urgency Level */}
              <div className="csr-form-group">
                <label className="csr-form-label">Urgency Level</label>
                <div className="csr-urgency-row">
                  {URGENCY_LEVELS.map(u => (
                    <button
                      key={u.value}
                      type="button"
                      className={`csr-urgency-pill csr-urgency-${u.value.toLowerCase()} ${urgencyLevel === u.value ? 'active' : ''}`}
                      onClick={() => setUrgencyLevel(u.value)}
                    >
                      {u.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Service Provider (optional — open-SR flow supports assign-later) */}
              <div className="csr-form-group">
                <label className="csr-form-label">
                  Service Provider <span className="csr-form-optional">(optional)</span>
                </label>
                <div className="csr-browse-wrapper">
                  <input
                    type="text"
                    className={`csr-form-input csr-browse-input ${errors.provider ? 'csr-input-error' : ''}`}
                    value={selectedProvider ? selectedProvider.fullName : ''}
                    readOnly
                    placeholder={serviceType ? 'Browse providers from your network' : 'Select a service type first'}
                    onClick={serviceType ? openProviderView : undefined}
                    style={{ cursor: serviceType ? 'pointer' : 'not-allowed' }}
                  />
                  <button
                    type="button"
                    className="csr-browse-btn"
                    onClick={openProviderView}
                    disabled={!serviceType}
                  >
                    {selectedProvider ? 'Change' : 'Browse'}
                  </button>
                </div>
                {errors.provider && <span className="csr-field-error">{errors.provider}</span>}
              </div>

              {/* Description (optional) */}
              <div className="csr-form-group">
                <label className="csr-form-label">
                  Service Description <span className="csr-form-optional">(optional)</span>
                </label>
                <textarea
                  className={`csr-form-input csr-form-textarea ${errors.description ? 'csr-input-error' : ''}`}
                  rows={4}
                  placeholder="Describe the service needed, access instructions, special requirements, etc."
                  value={description}
                  onChange={e => { setDescription(e.target.value); setErrors(p => ({ ...p, description: '' })); }}
                />
                {errors.description && <span className="csr-field-error">{errors.description}</span>}
              </div>

              {/* Estimated Amount (optional) */}
              <div className="csr-form-group">
                <label className="csr-form-label">Estimated Amount <span className="csr-form-optional">(optional)</span></label>
                <input
                  type="number"
                  className="csr-form-input"
                  placeholder="0.00"
                  inputMode="decimal"
                  min="0"
                  step="0.01"
                  value={estimatedAmount}
                  onChange={e => setEstimatedAmount(e.target.value)}
                />
              </div>

              {submitError && <p className="csr-submit-error">{submitError}</p>}
            </div>

            <div className="csr-action-buttons">
              <button
                type="button"
                className="csr-btn csr-btn-secondary"
                onClick={() => navigate('/dashboard')}
                disabled={submitting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="csr-btn csr-btn-primary"
                onClick={handleSubmit}
                disabled={submitting}
              >
                {submitting ? 'Submitting…' : 'Create Service Request'}
              </button>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default CreateServiceRequestPage;
